import { useRef, useState } from 'react';
import { Photo } from 'models/Photos';
import { useTranslation } from 'react-i18next';
import usePropertiesUpdatingAllowed from 'components/domain/property/usePropertiesUpdatingAllowed';
import useInvalidatePropertyPhotos from 'pages/property/photos/hooks/useInvalidatePropertyPhotos';
import { useOnClickOutside } from 'hooks/useOnClickOutside';
import usePhotoMutation from '../modals/usePhotoMutation';
import { PhotoDescriptionContainer } from '../PhotoGrid.styles';
import PropertyEditActionWrapper from '../../../../common/PropertyEditActionWrapper';

interface PhotoDescriptionProps {
  photo: Photo;
}

const PhotoDescription = ({ photo }: PhotoDescriptionProps) => {
  const initialDescription = photo.description;
  const [descriptionValue, setDescriptionValue] = useState(initialDescription);
  const [isDirty, setIsDirty] = useState(false);
  const { t } = useTranslation();
  const descriptionRef = useRef<HTMLTextAreaElement>(null);
  const { invalidatePropertyPhotos } = useInvalidatePropertyPhotos();
  const { updatePhoto } = usePhotoMutation();
  const propertiesUpdatingAllowed = usePropertiesUpdatingAllowed();

  const handleUpdatePhoto = async () => {
    await updatePhoto({
      updatedPhoto: {
        description: descriptionValue,
        pmpRoomUid: photo.pmpRoomUid,
      },
      photoUid: photo.uid,
    });
    setIsDirty(false);
    invalidatePropertyPhotos();
  };

  useOnClickOutside(descriptionRef, async () => {
    if (isDirty) {
      await handleUpdatePhoto();
    }
  });

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const { value } = e.target;

    if (value.length > 250) {
      return;
    }

    setDescriptionValue(value);
    if (value !== initialDescription) {
      setIsDirty(true);
    }
  };

  const handleKeyDown = async (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter') {
      await handleUpdatePhoto();
    }
  };

  return (
    <PhotoDescriptionContainer
      data-testid="photo-description"
      $isDisabled={!propertiesUpdatingAllowed}
    >
      <PropertyEditActionWrapper tooltipId="property-photo-description-edit-tooltip">
        <textarea
          aria-label={t('pageProperty.photos.clickToAddCaption')}
          name="description"
          id="description"
          value={descriptionValue || ''}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          placeholder={t('pageProperty.photos.clickToAddCaption')}
          ref={descriptionRef}
        />
      </PropertyEditActionWrapper>
    </PhotoDescriptionContainer>
  );
};

export default PhotoDescription;
